/**
 * transformUtils.ts
 * @deprecated Ce module n'est actuellement pas utilisé dans l'application (imported_by_total = 0).
 * Il est conservé pour référence future mais peut être supprimé si non nécessaire.
 *
 * P1.6 FIX: Centraliser les transformations (drag/resize/rotate)
 * Module unique pour gérer toutes les transformations de shapes
 * Évite la logique dispersée entre renderer / hooks / events
 */
import type { ShapeData } from '../types/ShapeTypes';
import type { TransformPayload } from '../types/TransformTypes';
import { useAppStore } from '../services/StateService';

/**
 * Applique une transformation partielle à une shape pendant l'interaction
 * (pendant drag/resize/rotate - mise à jour "live" du store)
 *
 * @param id - ID de la shape à transformer
 * @param transform - Transformation partielle (x, y, width, height, rotation)
 */
export function applyShapeTransform(id: string, transform: Partial<TransformPayload>): void {
  const store = useAppStore.getState();
  const shape = store.shapes.find(s => s.id === id);

  if (!shape) {
    console.warn(`[transformUtils] Shape ${id} not found`);
    return;
  }

  // Construire la transformation complète en fusionnant avec l'état actuel
  const fullTransform: TransformPayload = {
    x: transform.x ?? shape.x ?? 0,
    y: transform.y ?? shape.y ?? 0,
    width: transform.width ?? shape.width ?? 100,
    height: transform.height ?? shape.height ?? 100,
    rotation: transform.rotation ?? shape.rotation ?? 0,
  };

  // Appliquer la transformation via le store
  store.applyTransform(id, fullTransform);
}

/**
 * Commit une transformation à la fin de l'interaction
 * (fin drag/resize/rotate - sauvegarde dans l'historique)
 *
 * @param id - ID de la shape transformée
 * @param transform - Transformation finale (x, y, width, height, rotation)
 */
export function commitShapeTransform(id: string, transform: TransformPayload): void {
  const store = useAppStore.getState();
  const shape = store.shapes.find(s => s.id === id);

  if (!shape) {
    console.warn(`[transformUtils] Shape ${id} not found`);
    return;
  }

  // Appliquer la transformation finale
  store.applyTransform(id, transform);

  // Committer dans l'historique (1 seul snapshot pour toute la transformation)
  store.commitHistory();
}

/**
 * Applique une transformation à plusieurs shapes (batch)
 * Utile pour la multi-sélection
 *
 * @param ids - IDs des shapes à transformer
 * @param transform - Transformation à appliquer (peut être partielle)
 */
export function applyShapesTransform(ids: string[], transform: Partial<TransformPayload>): void {
  const store = useAppStore.getState();

  // Construire les updates pour updateShapes (batch)
  const updates = ids
    .map(id => {
      const shape = store.shapes.find(s => s.id === id);
      if (!shape) return null;

      const fullTransform: Partial<ShapeData> = {
        x: transform.x ?? shape.x,
        y: transform.y ?? shape.y,
        width: transform.width ?? shape.width,
        height: transform.height ?? shape.height,
        rotation: transform.rotation ?? shape.rotation,
      };

      return {
        id,
        updates: fullTransform,
      };
    })
    .filter((update): update is { id: string; updates: Partial<ShapeData> } => update !== null);

  if (updates.length === 0) return;

  // Appliquer les transformations en batch
  store.updateShapes(updates);
}

/**
 * Commit les transformations de plusieurs shapes à la fin de l'interaction
 *
 * @param ids - IDs des shapes transformées
 * @param transforms - Transformations finales (une par shape)
 */
export function commitShapesTransform(ids: string[], transforms: TransformPayload[]): void {
  if (ids.length !== transforms.length) {
    console.warn('[transformUtils] Mismatch between ids and transforms length');
    return;
  }

  const store = useAppStore.getState();

  // Construire les updates pour updateShapes (batch)
  const updates = ids.map((id, index) => ({
    id,
    updates: transforms[index] as Partial<ShapeData>,
  }));

  // Appliquer les transformations en batch
  store.updateShapes(updates);

  // Committer dans l'historique (1 seul snapshot pour toutes les transformations)
  store.commitHistory();
}
